import React, { useEffect, useState } from "react";
import {
  Grid,
  GridItem,
  Text,
  VStack,
  Box,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Button,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Portal,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { CaretDown, FadersHorizontal } from "@phosphor-icons/react";
import { useQueryClient } from "react-query";
import DashboardCard from "../../../ISSB/Dashboard/components/DashboardCard";
import LocalSpinner from "../../../../components/LocalSpinner";
import { getFormattedNumValue, showSuccessToast, showErrorToast } from "../../../../utils";
import { getYoYChange, yoyPair } from "../../../../utils/yoy";
import { useSearchParams } from "react-router-dom";
import { useGetReport, useGetCompanyTotalEmissionsReports, useGetMultiYearReports, useUpdateTurnover, reportKeys } from "../../../../hooks/query/reports";
import { scopeThreeCategories } from "../../../Reports/CompanyEmissionsDashboard/components/helpers";
import { useTranslation } from "react-i18next";
import useAirUserRoles from "../../../../utils/AuthUtils";
import YoYChip from "./YoYChip";

// Lime focus ring for form fields (project convention — never the default blue).
const LIME_FOCUS = {
  _hover: { borderColor: "gray.400" },
  _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
  _focusVisible: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
};

// Hover hint for clickable scope cards, styled like the shared chart tooltip
// card (white, subtle border + shadow) and following the cursor like the
// nivo chart tooltips do (a Chakra Tooltip would anchor to the card's
// bounding box instead). Renders children untouched when not clickable.
const DrillHint = ({ label, enabled = true, children }) => {
  const [pos, setPos] = useState(null);
  if (!enabled) return children;
  return (
    <Box
      height="100%"
      onMouseMove={(e) => setPos({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => setPos(null)}
    >
      {children}
      {pos && (
        <Portal>
          <Box
            position="fixed"
            left={`${pos.x}px`}
            top={`${pos.y - 14}px`}
            transform="translate(-50%, -100%)"
            pointerEvents="none"
            zIndex="tooltip"
            bg="white"
            color="#4A5568"
            border="1px solid"
            borderColor="gray.300"
            borderRadius="4px"
            boxShadow="0 1px 2px rgba(0,0,0,0.05)"
            fontSize="13px"
            px={3}
            py={2}
            whiteSpace="nowrap"
          >
            {label}
          </Box>
        </Portal>
      )}
    </Box>
  );
};

// ESG-3961: getYoYChange was defined here as well as in BigNumber.jsx, byte-identical. Both copies moved
// to utils/yoy.js.

// Big-number primitive used by every Total Emissions summary card. The font
// size is fluid: clamp() picks the largest value that fits the card's share
// of the viewport, so the number scales with screen width and never wraps
// onto a second line. Every card on the Total Emissions grid uses the SAME
// clamp — Location/Market figures stack vertically inside their card so
// each one gets the full card width, matching Scope 1 / Scope 3 sizing.
// Do NOT replace the var(--big) clamp with a static fontSize token (e.g.
// heading.2xl) — that's the regression the user has reverted multiple times.
//
// `pctChange` renders the YoY chip flush right of the number (matching the
// drill-down KpiCard layout) so the directional indicator stays adjacent to
// the figure it qualifies. Pass null/undefined to suppress the chip.
const BigNumber = ({ children, color = "gray.800", pctChange = null }) => (
  <Box display="inline-block" whiteSpace="nowrap" sx={{ "--big": "clamp(20px, 2.6vw, 48px)" }}>
    <Flex align="center" gap={2}>
      <Text
        fontSize="var(--big)"
        fontWeight="bold"
        color={color}
        lineHeight="1"
        letterSpacing="-0.02em"
      >
        {children}
      </Text>
      {pctChange != null && <YoYChip pctChange={pctChange} />}
    </Flex>
  </Box>
);

const SummaryCards = ({ currentYearData, previousYearData, isFetchingCurrentYear, isFetchingPreviousYear, selectedYear, quarter, dimensionActive = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // null = whole year; the /reports endpoints require a non-null quarter ("ALL").
  const reportQuarter = quarter || "ALL";

  const { data: currentReportData, isFetching: isFetchingCurrentReport } = useGetReport(
    { year: selectedYear, quarter: reportQuarter },
    { enabled: !!selectedYear }
  );
  const { data: previousReportData, isFetching: isFetchingPreviousReport } = useGetReport(
    { year: selectedYear - 1, quarter: reportQuarter },
    { enabled: !!selectedYear }
  );

  // Scope 3 comes from the PERSISTED report, not /insights/total — the ESG-3956 workaround documented on
  // EmissionsTrends.jsx. Both years must come from the same place: the chip previously paired this current
  // figure against previousYearData.scope3Total (/insights/total), so the percentage was a ratio between a
  // persisted snapshot and a live recompute. If the workaround is ever reverted, move BOTH sides back to
  // /insights/total together — the pair has to stay on one source in either direction.
  const { data: scope3Data, isFetching: isFetchingScope3 } = useGetCompanyTotalEmissionsReports(
    { year: selectedYear, quarter: reportQuarter },
    { enabled: !!selectedYear }
  );
  // Usually a cache hit: EmissionsTrends renders on this page and already fetches (selectedYear - 1, "ALL")
  // under the same query key.
  const { data: previousScope3Data, isFetching: isFetchingPreviousScope3 } = useGetCompanyTotalEmissionsReports(
    { year: selectedYear - 1, quarter: reportQuarter },
    { enabled: !!selectedYear }
  );
  // One field name, read off both payloads — see yoyPair for why that is the guard rather than a comment.
  const scopeThree = yoyPair(scope3Data, previousScope3Data, "scopeThreeTotalCo2Emissions");

  const handleClick = (scope) => {
    const params = new URLSearchParams(searchParams);
    params.set("detailView", scope);
    setSearchParams(params);
  };

  return (
    <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={6}>
      <GridItem>
        <DrillHint label="Click to drill down into Scope 1 details">
          <ScopeCard
            title="Scope 1"
            currentYear={currentYearData?.scope1Total}
            previousYear={previousYearData?.scope1Total}
            currentLoading={isFetchingCurrentYear}
            previousLoading={isFetchingPreviousYear}
            onClick={() => handleClick("scopeOne")}
          />
        </DrillHint>
      </GridItem>
      <GridItem>
        <DrillHint label="Click to drill down into Scope 2 details">
          <Scope2CombinedCard
            locationCurrent={currentYearData?.scope2LocationTotal}
            locationPrevious={previousYearData?.scope2LocationTotal}
            marketCurrent={currentYearData?.scope2MarketTotal}
            marketPrevious={previousYearData?.scope2MarketTotal}
            currentLoading={isFetchingCurrentYear}
            previousLoading={isFetchingPreviousYear}
            onClick={() => handleClick("scopeTwo")}
          />
        </DrillHint>
      </GridItem>
      <GridItem rowSpan={2}>
        <DrillHint label="Click to drill down into Scope 3 details">
          <Scope3Card
            currentYear={scopeThree.current}
            previousYear={scopeThree.previous}
            currentLoading={isFetchingScope3}
            previousLoading={isFetchingPreviousScope3}
            scope3Data={scope3Data}
            isFetchingScope3={isFetchingScope3}
            dimensionActive={dimensionActive}
            onClick={() => handleClick("scopeThree")}
          />
        </DrillHint>
      </GridItem>
      <GridItem>
        <CarbonIntensityCard
          currentReportData={currentReportData}
          previousReportData={previousReportData}
          currentLoading={isFetchingCurrentReport}
          previousLoading={isFetchingPreviousReport}
          quarter={reportQuarter}
          dimensionActive={dimensionActive}
        />
      </GridItem>
      <GridItem>
        <Scope1And2CombinedCard
          locationCurrent={currentYearData?.scope1AndScope2LocationTotal}
          locationPrevious={previousYearData?.scope1AndScope2LocationTotal}
          marketCurrent={currentYearData?.scope1AndScope2MarketTotal}
          marketPrevious={previousYearData?.scope1AndScope2MarketTotal}
          currentLoading={isFetchingCurrentYear}
          previousLoading={isFetchingPreviousYear}
        />
      </GridItem>
    </Grid>
  );
};

const Scope3Card = ({
  currentYear,
  previousYear,
  currentLoading,
  previousLoading,
  scope3Data,
  isFetchingScope3,
  dimensionActive = false,
  onClick,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dataEntryPlanning.modal.categories",
  });

  const percentageChange =
    !currentLoading && !previousLoading
      ? getYoYChange(currentYear, previousYear)
      : null;

  return (
    <DashboardCard onClick={onClick} bg="#F7FAFC">
      <Text fontSize="body.2xl" fontWeight="bold" color="teal">
        Scope 3{" "}
        <Text as="span" fontSize="body.s" fontWeight="bold" color="gray.700">
          (tCO2e)
        </Text>
      </Text>
      {currentLoading || previousLoading ? (
        <LocalSpinner size="sm" color="darkTeal" />
      ) : (
        <BigNumber pctChange={percentageChange}>
          {getFormattedNumValue(currentYear, 2, true) || 0}
        </BigNumber>
      )}
      {/* ESG-3961: /reports/total-emissions takes no dimension params, so this figure and the category
          breakdown below it are org-wide while the Scope 1 and Scope 2 cards beside them ARE filtered.
          Saying so beats letting the reader assume the filter applied — the same call CarbonIntensityCard
          makes, which blanks its ratios under a filter rather than showing a whole-org number unlabelled.
          Retire this when Scope 3 moves back to /insights/total. */}
      {dimensionActive && !currentLoading && (
        <Text fontSize="body.xs" color="gray.500" mt={1}>
          Whole organisation — the dimension filter does not apply to Scope 3.
        </Text>
      )}
      <Box borderTop="1px solid" borderColor="gray.200" mt={2} pt={2}>
        {isFetchingScope3 ? (
          <LocalSpinner size="sm" color="darkTeal" />
        ) : (
          <VStack spacing={0} align="stretch">
            {scopeThreeCategories.map((cat, index) => {
              const value = scope3Data?.[`category${cat.numberKey}TotalCo2Emissions`];
              if (!value || value <= 0) return null;
              return (
                <React.Fragment key={cat.numberKey}>
                  <Flex
                    justify="space-between"
                    align="center"
                    py={1.5}
                    borderBottom="1px solid"
                    borderColor="gray.100"
                  >
                    <Text fontSize="body.xs" color="gray.700">
                      {cat.index}. {t(cat.translationKey)}
                    </Text>
                    <Text fontSize="body.xs" fontWeight="bold" color="gray.800" flexShrink={0} ml={2}>
                      {value?.toLocaleString("en-GB", { maximumFractionDigits: 2 }) ?? ""}
                    </Text>
                  </Flex>
                </React.Fragment>
              );
            })}
          </VStack>
        )}
      </Box>
    </DashboardCard>
  );
};

const INTENSITY_OPTIONS = [
  {
    label: "Turnover",
    location: "carbonIntensityRatioGrossLocationBased",
    market: "carbonIntensityRatioGrossMarketBased",
    unit: "tCO₂e / £m",
  },
  {
    label: "Employees",
    location: "carbonIntensityRatioPerEmployeeLocationBased",
    market: "carbonIntensityRatioPerEmployeeMarketBased",
    unit: "tCO₂e / FTE",
    // Per-employee intensity isn't supported by the backend yet — offered as
    // a disabled "coming soon" option so the intent is visible.
    comingSoon: true,
  },
];

const CarbonIntensityCard = ({
  currentReportData,
  previousReportData,
  currentLoading = false,
  previousLoading = false,
  quarter,
  dimensionActive = false,
}) => {
  const [selectedIntensity, setSelectedIntensity] = useState(INTENSITY_OPTIONS[0]);
  const { isOrgAdmin, isESGAdmin } = useAirUserRoles();
  const settingsModal = useDisclosure();

  // Read the real report-level intensity ratios. The ratios live under the
  // report's scopeOneAndTwoTotal block (not the top level). When the org hasn't
  // provided the denominator (turnover / headcount) the backend has no ratio,
  // so the value is null → rendered as "N/A". We deliberately do NOT fabricate
  // an intensity from an assumed denominator (ESG-3932).
  const currentTotals = currentReportData?.scopeOneAndTwoTotal;
  const previousTotals = previousReportData?.scopeOneAndTwoTotal;

  // Intensity ratios are org-wide (the report fetch ignores the dimension
  // filter), so they don't correspond to a filtered subset — force N/A while a
  // dimension filter is active rather than showing a misleading whole-org ratio.
  const locationCurrent = dimensionActive ? null : currentTotals?.[selectedIntensity.location] ?? null;
  const locationPrevious = dimensionActive ? null : previousTotals?.[selectedIntensity.location] ?? null;
  const marketCurrent = dimensionActive ? null : currentTotals?.[selectedIntensity.market] ?? null;
  const marketPrevious = dimensionActive ? null : previousTotals?.[selectedIntensity.market] ?? null;

  // No real ratio for either basis → either a dimension filter is active or the
  // denominator is missing. Show a hint explaining which.
  const denominatorMissing = locationCurrent == null && marketCurrent == null;
  const missingHint = dimensionActive
    ? "Not available when filtered by dimension"
    : selectedIntensity.comingSoon
      ? "coming soon"
      : "turnover not provided";

  const cardLoading = currentLoading || previousLoading;

  const locationChange = getYoYChange(locationCurrent, locationPrevious);
  const marketChange = getYoYChange(marketCurrent, marketPrevious);

  // Org admins can set the turnover denominator inline. Only offer it when a
  // report exists for the selected period and no dimension filter is active
  // (the denominator is org-wide, per year).
  const canEditDenominators = (isOrgAdmin || isESGAdmin) && !!currentReportData?.id && !dimensionActive;

  return (
    <Box position="relative" height="100%">
      {/* Denominator dropdown + settings icon cluster in the top-right corner
          of the card, matching where DashboardCard renders the drill-down
          arrow. This card has no drill-down, so the cluster sits there alone. */}
      <HStack position="absolute" top={2} right={2} zIndex={1} spacing={1}>
        <Menu>
          <MenuButton
            as={Button}
            size="xs"
            borderRadius="full"
            border="1px solid"
            borderColor="gray.300"
            bg="transparent"
            color="gray.600"
            fontWeight="normal"
            rightIcon={<CaretDown size={12} />}
            _hover={{ bg: "gray.50", borderColor: "gray.400" }}
            px={3}
            h="24px"
            fontSize="11px"
          >
            {selectedIntensity.label}
          </MenuButton>
          <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1} minW="120px">
            {INTENSITY_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.label}
                onClick={() => setSelectedIntensity(opt)}
                isDisabled={opt.comingSoon}
                fontWeight={selectedIntensity.label === opt.label ? "bold" : "normal"}
                fontSize="sm"
              >
                <Flex align="center" gap={2} w="full" justify="space-between">
                  <Text>{opt.label}</Text>
                  {opt.comingSoon && (
                    <Text fontSize="10px" color="gray.400" fontStyle="italic">
                      coming soon
                    </Text>
                  )}
                </Flex>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
        {canEditDenominators && (
          <IconButton
            aria-label="Set carbon intensity inputs"
            icon={<FadersHorizontal size={16} />}
            size="xs"
            variant="ghost"
            color="gray.500"
            borderRadius="full"
            h="24px"
            minW="24px"
            _hover={{ bg: "gray.100", color: "gray.700" }}
            onClick={settingsModal.onOpen}
          />
        )}
      </HStack>
    <DashboardCard bg="#F7FAFC" px={6}>
      <Flex justify="space-between" align="center">
        <Text fontSize="body.2xl" fontWeight="bold" color="teal">
          Carbon Intensity{" "}
          <Text as="span" fontSize="body.s" fontWeight="bold" color="gray.700">
            ({selectedIntensity.unit})
          </Text>
        </Text>
      </Flex>
      {canEditDenominators && (
        <IntensityDenominatorsModal
          isOpen={settingsModal.isOpen}
          onClose={settingsModal.onClose}
          quarter={quarter}
        />
      )}
      {denominatorMissing && !cardLoading && (
        <Text fontSize="11px" color="gray.500" mt={-1} mb={1}>
          {missingHint}
        </Text>
      )}
      <VStack align="stretch" spacing={4} flex={1}>
        <Box>
          {cardLoading ? (
            <LocalSpinner size="sm" color="darkTeal" />
          ) : (
            <BigNumber pctChange={locationChange}>
              {getFormattedNumValue(locationCurrent, 2, true)}
            </BigNumber>
          )}
          <Text fontSize="body.s" fontWeight="semibold" color="gray.500">
            Location
          </Text>
        </Box>
        <Box borderTop="1px solid" borderColor="gray.200" pt={4}>
          {cardLoading ? (
            <LocalSpinner size="sm" color="darkTeal" />
          ) : (
            <BigNumber pctChange={marketChange}>
              {getFormattedNumValue(marketCurrent, 2, true)}
            </BigNumber>
          )}
          <Text fontSize="body.s" fontWeight="semibold" color="gray.500">
            Market
          </Text>
        </Box>
      </VStack>
    </DashboardCard>
    </Box>
  );
};

// Descending list of reporting years the overlay can edit — current year back
// 5 (matches the FootPrint year range, minus the forward-looking years which
// won't have reports yet).
const TURNOVER_YEARS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

// Org-admin overlay to set the turnover denominator that drives the carbon
// intensity ratios. Turnover is stored per reporting year (per report), so the
// overlay lists every year that has a report and lets the user add/change the
// turnover for each. Employee-based intensity is not yet supported by the
// backend, so there is no headcount field here.
const IntensityDenominatorsModal = ({ isOpen, onClose, quarter }) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  // One report per year (fixed quarter). Only fetch while the modal is open.
  const reportQueries = useGetMultiYearReports(TURNOVER_YEARS, quarter, {
    enabled: isOpen,
    retry: false,
    cacheTime: 60_000,
  });

  // Rows: only years that actually have a report (turnover is stored on the
  // report, so a year with no report can't hold one).
  const rows = TURNOVER_YEARS.map((year, i) => {
    const q = reportQueries[i];
    const report = q?.data;
    return report?.id
      ? { year, reportId: report.id, turnover: report.scopeOneAndTwoTotal?.turnover ?? null }
      : null;
  }).filter(Boolean);

  const loading = reportQueries.some((q) => q.isLoading);

  // Draft edits keyed by year. Primed from the fetched turnover; re-primed each
  // time the modal opens or the fetched data changes.
  const [drafts, setDrafts] = useState({});
  const primeKey = rows.map((r) => `${r.year}:${r.turnover}`).join("|");
  useEffect(() => {
    if (!isOpen) return;
    const next = {};
    rows.forEach((r) => { next[r.year] = r.turnover != null ? String(r.turnover) : ""; });
    setDrafts(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, primeKey]);

  const { mutateAsync: updateTurnover, isLoading: isSaving } = useUpdateTurnover({
    onError: (err) => showErrorToast(toast, err?.response?.data?.parameters?.message || err.message),
  });

  // A row is dirty when its draft differs from the stored value, and valid.
  const dirtyRows = rows.filter((r) => {
    const draft = (drafts[r.year] ?? "").trim();
    const stored = r.turnover != null ? String(r.turnover) : "";
    if (draft === stored) return false;
    if (draft === "") return false; // blank = no change (we don't clear turnover)
    const n = Number(draft);
    return Number.isFinite(n) && n >= 0;
  });

  const anyInvalid = rows.some((r) => {
    const draft = (drafts[r.year] ?? "").trim();
    if (draft === "") return false;
    const n = Number(draft);
    return !Number.isFinite(n) || n < 0;
  });

  const handleSave = async () => {
    if (dirtyRows.length === 0 || anyInvalid) return;
    try {
      await Promise.all(
        dirtyRows.map((r) => updateTurnover({ reportId: r.reportId, turnover: Number(drafts[r.year]) }))
      );
      showSuccessToast(toast, dirtyRows.length > 1 ? "Turnover updated" : `Turnover updated for ${dirtyRows[0].year}`);
      // Refresh each edited year's report so the card picks up the new ratio.
      dirtyRows.forEach((r) => queryClient.invalidateQueries([...reportKeys.reportData(), r.year, quarter]));
      onClose();
    } catch {
      // per-mutation onError already surfaced a toast
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">Carbon intensity inputs</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.500" mb={4}>
            Enter the total turnover for your whole organisation for each reporting year. It is used
            to calculate the gross carbon intensity ratio (tCO₂e per £m turnover).
          </Text>
          {loading ? (
            <Flex justify="center" py={6}>
              <LocalSpinner size="sm" color="darkTeal" />
            </Flex>
          ) : rows.length === 0 ? (
            <Text fontSize="sm" color="gray.500" py={4}>
              No reporting years are available yet.
            </Text>
          ) : (
            <VStack align="stretch" spacing={3}>
              {rows.map((r) => (
                <FormControl key={r.year}>
                  <FormLabel fontSize="sm" mb={1}>{r.year}</FormLabel>
                  <InputGroup size="sm">
                    <Input
                      type="number"
                      min={0}
                      value={drafts[r.year] ?? ""}
                      onChange={(e) => setDrafts((prev) => ({ ...prev, [r.year]: e.target.value }))}
                      placeholder="e.g. 120"
                      {...LIME_FOCUS}
                    />
                    <InputRightAddon>£m</InputRightAddon>
                  </InputGroup>
                </FormControl>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            bg="darkTeal"
            color="white"
            isDisabled={dirtyRows.length === 0 || anyInvalid}
            isLoading={isSaving}
            onClick={handleSave}
            _hover={{ opacity: 0.9 }}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const Scope1And2CombinedCard = ({
  locationCurrent,
  locationPrevious,
  marketCurrent,
  marketPrevious,
  currentLoading = false,
  previousLoading = false,
}) => {
  const locationChange = getYoYChange(locationCurrent, locationPrevious);
  const marketChange = getYoYChange(marketCurrent, marketPrevious);

  return (
    <DashboardCard bg="#F7FAFC" px={6}>
      <Text fontSize="body.2xl" fontWeight="bold" color="teal">
        Scope 1 and 2 Total{" "}
        <Text as="span" fontSize="body.s" fontWeight="bold" color="gray.700">
          (tCO2e)
        </Text>
      </Text>
      <VStack align="stretch" spacing={4} flex={1}>
        <Box>
          {currentLoading || previousLoading ? (
            <LocalSpinner size="sm" color="darkTeal" />
          ) : (
            <BigNumber pctChange={locationChange}>
              {getFormattedNumValue(locationCurrent, 2, true) || 0}
            </BigNumber>
          )}
          <Text fontSize="body.s" fontWeight="semibold" color="gray.500">
            Location
          </Text>
        </Box>
        <Box borderTop="1px solid" borderColor="gray.200" pt={4}>
          {currentLoading || previousLoading ? (
            <LocalSpinner size="sm" color="darkTeal" />
          ) : (
            <BigNumber pctChange={marketChange}>
              {getFormattedNumValue(marketCurrent, 2, true) || 0}
            </BigNumber>
          )}
          <Text fontSize="body.s" fontWeight="semibold" color="gray.500">
            Market
          </Text>
        </Box>
      </VStack>
    </DashboardCard>
  );
};

const Scope2CombinedCard = ({
  locationCurrent,
  locationPrevious,
  marketCurrent,
  marketPrevious,
  currentLoading = false,
  previousLoading = false,
  onClick,
}) => {
  const locationChange = getYoYChange(locationCurrent, locationPrevious);
  const marketChange = getYoYChange(marketCurrent, marketPrevious);

  return (
    <DashboardCard onClick={onClick} bg="#F7FAFC" px={6}>
      <Text fontSize="body.2xl" fontWeight="bold" color="teal">
        Scope 2{" "}
        <Text as="span" fontSize="body.s" fontWeight="bold" color="gray.700">
          (tCO2e)
        </Text>
      </Text>
      <VStack align="stretch" spacing={4} flex={1}>
        <Box>
          {currentLoading || previousLoading ? (
            <LocalSpinner size="sm" color="darkTeal" />
          ) : (
            <BigNumber pctChange={locationChange}>
              {getFormattedNumValue(locationCurrent, 2, true) || 0}
            </BigNumber>
          )}
          <Text fontSize="body.s" fontWeight="semibold" color="gray.500">
            Location
          </Text>
        </Box>
        <Box borderTop="1px solid" borderColor="gray.200" pt={4}>
          {currentLoading || previousLoading ? (
            <LocalSpinner size="sm" color="darkTeal" />
          ) : (
            <BigNumber pctChange={marketChange}>
              {getFormattedNumValue(marketCurrent, 2, true) || 0}
            </BigNumber>
          )}
          <Text fontSize="body.s" fontWeight="semibold" color="gray.500">
            Market
          </Text>
        </Box>
      </VStack>
    </DashboardCard>
  );
};

export const ScopeCard = ({
  title,
  currentYear,
  previousYear,
  currentLoading = false,
  previousLoading = false,
  onClick,
  theme = "light",
  calculatePercentageChange = true,
  unit = "tCO2e",
}) => {
  // Define local theme colors
  const colors = {
    light: {
      cardBg: "#F7FAFC",
      title: "teal",
      value: "gray.800",
      percentUp: "status.red",
      percentDown: "status.green",
      spinnerColor: "darkTeal",
    },
    dark: {
      cardBg: "pureBlack",
      title: "pureWhite",
      value: "lightLime",
      percentUp: "status.red",
      percentDown: "status.green",
      spinnerColor: "lightLime",
    },
  };

  const t = colors[theme] || colors.light;

  // When calculatePercentageChange is false, the caller is passing a
  // pre-computed % to display verbatim (used by SupplierInsightEmissionDetail).
  // Otherwise compute YoY from the two values.
  const percentageChange = !calculatePercentageChange
    ? previousYear
    : !currentLoading && !previousLoading
      ? getYoYChange(currentYear, previousYear)
      : null;

  return (
    <DashboardCard onClick={onClick} bg={t.cardBg} px={6}>
      <Text fontSize="body.2xl" fontWeight="bold" color={t.title}>
        {title}{" "}
        <Text as="span" fontSize="body.s" fontWeight="bold" color={theme === "dark" ? "gray.300" : "gray.700"}>
          ({unit})
        </Text>
      </Text>
      {currentLoading || previousLoading ? (
        <LocalSpinner size="sm" color={t.spinnerColor} />
      ) : (
        <BigNumber color={t.value} pctChange={percentageChange}>
          {getFormattedNumValue(currentYear, 2, true) || 0}
        </BigNumber>
      )}
    </DashboardCard>
  );
};

export default SummaryCards;
